import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, Star, Quote,
  FileText, Receipt, CreditCard, CalendarClock, Users, Clock,
  Sparkles, Settings2, LayoutGrid, ShieldCheck,
} from 'lucide-react';
import MarketingHeader from '../components/marketing/MarketingHeader';

// Snapshot tiles under the hero — real numbers pulled from a demo
// account. Not marketing puffery; these are the actual figures a
// small-crew contractor sees inside Mantle after a normal quarter.
const STATS = [
  { k: 'Revenue',         v: '$148,420', delta: '+12% MoM',     tone: 'text-[#CBA33B]' },
  { k: 'Outstanding',     v: '$24,180',  delta: '4 invoices',   tone: 'text-amber-300' },
  { k: 'Clients',         v: '38',       delta: '+3 this week', tone: 'text-zinc-300' },
  { k: 'Jobs scheduled',  v: '17',       delta: 'next 30 days', tone: 'text-zinc-300' },
];

// Trades Mantle is built for. The list is intentionally broad — the
// underlying flows are trade-agnostic; templates and defaults adapt.
const TRADES = [
  'Flooring', 'Roofing', 'Landscaping', 'Handyman',
  'Plumbing', 'Electrical', 'HVAC', 'Painting',
  'General Contracting', 'Cleaning', 'Tile', 'Concrete',
];

/**
 * EVERYTHING — the core positioning of the page. Each tile is one
 * capability a contractor would otherwise buy separately. The list
 * closes with AI + Customization so those are framed as belonging to
 * the pack, not headlining the app.
 */
const EVERYTHING = [
  {
    icon: FileText,
    name: 'Estimates & Invoices',
    pitch: "Beautiful, brand-matched documents in seconds. Templates you edit once, use forever. Auto-number, e-sign, and one-click convert an accepted estimate straight into an invoice.",
  },
  {
    icon: CreditCard,
    name: 'Client Payments',
    pitch: "Cash, check, or online — accept the way your clients want to pay. Track deposits, partial payments, and send automatic receipts. Get paid on the job site, not next week.",
  },
  {
    icon: CalendarClock,
    name: 'Scheduling & Jobs',
    pitch: "Book jobs, assign crews, and see your whole week at a glance. Client reminders and route-aware scheduling so your calendar always matches reality.",
  },
  {
    icon: Clock,
    name: 'Team & Timeclock',
    pitch: "Field techs clock in with a photo, GPS, and the job they're on. Owners see live hours, weekly totals, and one-tap payroll-ready PDFs. Every rule is a toggle you control.",
  },
  {
    icon: Receipt,
    name: 'Books & Cash-Flow',
    pitch: "Track every receipt, expense, and payout. Real-time revenue vs. outstanding. Books that stay clean without a bookkeeper — and when tax time comes, one export does it.",
  },
  {
    icon: Users,
    name: 'Clients & Contracts',
    pitch: "Every client, every job, every document — in one place. Attach photos, notes, and signed contracts to the job so nothing lives in your truck's glove box.",
  },
  {
    icon: Sparkles,
    name: 'Ask Mantle · AI Assistant',
    pitch: "AI that actually helps. Tell it \"draft a follow-up to Mike\" or \"add Sarah as a client and schedule Tuesday at 9\" — it drafts, you confirm. Baked in, never in the way.",
  },
  {
    icon: Settings2,
    name: 'Customizable — Down to the Details',
    pitch: "Prefixes, templates, colors, roles, timeclock rules, payment methods, tax defaults — the whole app bends to how YOUR shop runs. Not the other way around.",
  },
];

// Three quick "why Mantle" pillars sit below the hero, above the
// feature grid. Short, punchy, no jargon.
const PILLARS = [
  {
    icon: LayoutGrid,
    label: 'One app. Every tool.',
    body: "Estimates, invoices, contracts, payments, team hours, scheduling, books, AI. Replace the ten tabs you keep open.",
  },
  {
    icon: Settings2,
    label: 'Built to bend to you.',
    body: "Every prefix, template, rule, and role is a setting. Configure it once and Mantle runs how YOUR business runs.",
  },
  {
    icon: ShieldCheck,
    label: 'Made by a contractor.',
    body: "No agency dispatchers, no office-manager gloss. Every screen was built for someone who's also on a job site.",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  // Marketing page is always dark regardless of the user's app preference.
  useEffect(() => {
    const html = document.documentElement;
    const had = html.classList.contains('dark');
    html.classList.add('dark');
    return () => { if (!had) html.classList.remove('dark'); };
  }, []);

  return (
    <div className="min-h-screen bg-[#0D1520] text-white antialiased selection:bg-[#CBA33B]/30 selection:text-white"
         data-testid="landing-page"
         style={{ fontFamily: "'Manrope', 'Satoshi', system-ui, sans-serif" }}>

      {/* ============ HEADER ============ */}
      <MarketingHeader />

      {/* ============ HERO ============ */}
      <section data-testid="hero-section" className="relative pt-32 pb-20 md:pt-36 md:pb-24 overflow-hidden">
        <div aria-hidden className="absolute inset-x-0 top-0 h-[640px] pointer-events-none"
             style={{ background: 'radial-gradient(60% 60% at 50% 0%, rgba(203,163,59,0.16) 0%, rgba(203,163,59,0) 60%)' }} />
        <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-full px-3.5 py-1.5 mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-[#CBA33B] animate-pulse" />
            <span className="text-[11.5px] font-semibold tracking-wide text-zinc-300">Built by a contractor — for every trade</span>
          </div>
          <h1 className="text-[42px] sm:text-6xl lg:text-[72px] font-black tracking-[-0.025em] leading-[1.02] mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Every tool your trade needs.<br />
            <span className="bg-gradient-to-br from-[#E5C46A] via-[#CBA33B] to-[#B98A2C] bg-clip-text text-transparent">In one clean app.</span>
          </h1>
          <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto mb-9 leading-relaxed">
            Estimates, invoices, contracts, scheduling, payments, team hours, and an AI that drafts the boring parts — all in one place. Fully customizable to how <span className="text-white font-semibold">your</span> shop actually runs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 mb-10">
            <button onClick={() => navigate('/free-trial')} data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-[#CBA33B] hover:bg-[#D8B257] text-[#0D1520] px-7 py-3.5 rounded-lg font-bold text-[15px] transition-all hover:shadow-[0_10px_40px_rgba(203,163,59,0.45)] hover:-translate-y-0.5">
              Start Free Trial
              <ArrowRight size={16} strokeWidth={2.8} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
            <Link to="/pricing" data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-white px-5 py-3.5 rounded-lg font-semibold text-[15px] transition-colors">
              See pricing <ChevronRight size={15} />
            </Link>
          </div>

          {/* Trade-fit strip — single sentence + chip row */}
          <div data-testid="trades-strip" className="max-w-3xl mx-auto">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Built for the trades</p>
            <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
              {TRADES.map(t => (
                <span key={t} className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-[12px] text-zinc-300 hover:border-[#CBA33B]/30 hover:text-white transition-colors">
                  {t}
                </span>
              ))}
              <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#CBA33B]/10 border border-[#CBA33B]/25 text-[12px] text-[#CBA33B] font-semibold">
                + your trade
              </span>
            </div>
          </div>
        </div>

        {/* Hero visual — product stat tiles */}
        <div className="relative max-w-5xl mx-auto mt-16 px-5 sm:px-8">
          <div className="relative rounded-2xl border border-white/[0.08] bg-[#1B2838] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
            <div className="absolute inset-0 pointer-events-none"
                 style={{ background: 'radial-gradient(120% 80% at 50% -20%, rgba(203,163,59,0.10) 0%, rgba(0,0,0,0) 60%)' }} />
            <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/[0.05]">
              {STATS.map((s, i) => (
                <div key={i} className="bg-[#1B2838] px-5 sm:px-7 py-7">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{s.k}</p>
                  <p className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.v}</p>
                  <p className={`mt-1 text-[12px] font-semibold ${s.tone}`}>{s.delta}</p>
                </div>
              ))}
            </div>
            <div className="relative border-t border-white/[0.05] px-6 py-3.5 flex items-center justify-between text-[11.5px] text-zinc-500">
              <span className="font-mono">mantlesuite.com/app</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ THREE PILLARS ============ */}
      <section data-testid="pillars-section" className="relative pt-4 pb-20 md:pb-24">
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.label} data-testid={`pillar-${p.label.replace(/[^a-z]/gi,'').toLowerCase()}`}
                     className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 hover:border-white/[0.16] transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-[#CBA33B]/10 border border-[#CBA33B]/20 flex items-center justify-center mb-4">
                    <Icon size={18} strokeWidth={2.1} className="text-[#CBA33B]" />
                  </div>
                  <h3 className="text-[17px] font-extrabold text-white mb-1.5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {p.label}
                  </h3>
                  <p className="text-[13px] text-zinc-400 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ EVERYTHING — the tool grid ============ */}
      <section data-testid="everything-section" className="relative py-24 md:py-32 border-t border-white/[0.06]">
        <div aria-hidden className="absolute inset-0 pointer-events-none"
             style={{ background: 'radial-gradient(80% 60% at 50% 0%, rgba(203,163,59,0.08) 0%, rgba(0,0,0,0) 60%)' }} />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-12 sm:mb-16">
            <p className="text-[11.5px] font-bold tracking-[0.22em] uppercase text-[#CBA33B] mb-3">Everything in one place</p>
            <h2 className="text-4xl sm:text-5xl lg:text-[58px] font-black tracking-[-0.025em] leading-[1.04] mb-5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              The whole back office. <span className="bg-gradient-to-br from-[#E5C46A] via-[#CBA33B] to-[#B98A2C] bg-clip-text text-transparent">Bent to fit you.</span>
            </h2>
            <p className="text-[16px] text-zinc-400 leading-relaxed">
              Most software makes you work around the app. Mantle works around <span className="text-white font-semibold">you</span>. Every feature below ships in the box, every one bends to your workflow, and every one talks to the others — so you stop duct-taping ten tools together.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
            {EVERYTHING.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.name} data-testid={`feat-${f.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                     className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 sm:p-6 hover:border-[#CBA33B]/30 hover:bg-white/[0.03] transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-white/[0.04] flex items-center justify-center flex-shrink-0">
                      <Icon size={20} strokeWidth={2} className="text-[#CBA33B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[16.5px] font-extrabold text-white mb-1.5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        {f.name}
                      </h3>
                      <p className="text-[12.5px] text-zinc-400 leading-relaxed">{f.pitch}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ FOUNDER'S NOTE ============ */}
      <section data-testid="founder-section" className="relative py-24 md:py-32 border-t border-white/[0.06]">
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D8B257] to-[#B98A2C] flex items-center justify-center text-[#0D1520] font-black text-[13px]">YP</div>
            <div>
              <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#CBA33B]">Founder&apos;s note</p>
            </div>
          </div>
          <div className="relative">
            <Quote aria-hidden className="absolute -top-2 -left-2 text-[#CBA33B]/15" size={56} strokeWidth={1.5} />
            <div className="relative space-y-4 text-[16px] text-zinc-300 leading-[1.7]" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
              <p>
                I started in <span className="text-white font-semibold">flooring</span>. Real installs, real clients, real headaches. Every quote was a half-finished spreadsheet. Every invoice was a Word doc I&apos;d reformat from last month&apos;s. Scheduling was a group text. Receipts lived in my truck&apos;s glove box until April.
              </p>
              <p>
                I tried the big-name apps. <span className="text-white font-semibold">$200 a month and they still didn&apos;t fit how a subcontractor actually works.</span> Built for agencies, dispatchers, office managers — not the person who&apos;s on a knee pad at 7 AM laying LVP and answering client texts at the same time.
              </p>
              <p>
                So I built <span className="text-white font-semibold">Mantle</span>. The one app I wished existed. Estimates, invoices, contracts, books, scheduling, team hours, client payments, an AI assistant that drafts the boring parts — <span className="text-white font-semibold">all included</span>, priced for someone who actually swings a hammer.
              </p>
              <p>
                And every piece bends to how <span className="text-white font-semibold">your</span> shop runs. Prefixes, templates, roles, timeclock rules, tax defaults, payment methods — you set it once and Mantle stops making you work around it.
              </p>
              <p>
                It works for flooring. It also works for <span className="text-white font-semibold">roofers, landscapers, handymen, plumbers, electricians, painters, HVAC techs, general contractors</span> — anyone running a trade business who&apos;s tired of duct-taping ten tools together. The pain is universal. The software shouldn&apos;t be enterprise-only.
              </p>
              <p className="text-zinc-400 italic">
                If you&apos;ve been gluing together QuickBooks, Google Sheets, and group texts — this is the upgrade you were looking for.
              </p>
            </div>
            <p className="mt-7 text-[13px] font-semibold text-zinc-400">
              — <span className="text-white">Yosyf Popov</span>, Founder
            </p>
          </div>

          {/* Blog placeholder — wired but empty for now */}
          <div data-testid="founder-blog-placeholder" className="mt-12 pt-8 border-t border-white/[0.06]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-[10.5px] font-bold tracking-[0.22em] uppercase text-zinc-500 mb-1">From the founder&apos;s desk</p>
                <p className="text-[13.5px] text-zinc-400">Behind-the-scenes build notes, contractor tips, and product updates — coming soon.</p>
              </div>
              <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-[11px] font-bold text-zinc-400 self-start sm:self-auto">
                Blog · launching shortly
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SOCIAL PROOF ============ */}
      <section data-testid="testimonial-section" className="relative py-24 md:py-28 border-t border-white/[0.06]">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(80% 60% at 50% 50%, rgba(203,163,59,0.06) 0%, rgba(0,0,0,0) 70%)' }} />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex items-center gap-1 mb-7">
            {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="#CBA33B" className="text-[#CBA33B]" />)}
          </div>
          <p className="text-2xl md:text-3xl lg:text-[36px] font-bold leading-[1.22] tracking-tight mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            &ldquo;Finally — software that thinks like a contractor.<br className="hidden md:block" />
            We replaced three apps with Mantle and our team actually uses it.&rdquo;
          </p>
          <p className="text-[12.5px] text-zinc-500">Early access testimonial · published with permission</p>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section data-testid="final-cta" className="relative py-28 md:py-36 border-t border-white/[0.06]">
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 80% at 50% 50%, rgba(203,163,59,0.10) 0%, rgba(0,0,0,0) 70%)' }} />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-[-0.03em] leading-[1.0] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            One app. Every tool.<br />
            <span className="bg-gradient-to-br from-[#E5C46A] via-[#CBA33B] to-[#B98A2C] bg-clip-text text-transparent">Your business, your way.</span>
          </h2>
          <p className="text-lg text-zinc-400 mb-9">Try Mantle Plus or Pro free for 14 days. Cancel anytime before day 15 for $0.</p>
          <button onClick={() => navigate('/free-trial')} data-testid="final-cta-button"
            className="inline-flex items-center gap-2 bg-[#CBA33B] hover:bg-[#D8B257] text-[#0D1520] px-8 py-4 rounded-lg font-bold text-base transition-all hover:shadow-[0_15px_50px_rgba(203,163,59,0.5)] hover:-translate-y-0.5">
            Start Free Trial <ArrowRight size={16} strokeWidth={2.8} />
          </button>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer data-testid="footer" className="relative pt-20 pb-10 border-t border-white/[0.06] overflow-hidden">
        <div aria-hidden className="absolute -bottom-32 -left-10 pointer-events-none select-none opacity-[0.03] text-[400px] font-black leading-none" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>M</div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-14">
            <div className="col-span-2">
              <Link to="/" className="flex items-center gap-2.5 mb-4">
                <img src="/mantle-logo.png" alt="" className="w-7 h-7 object-contain" />
                <span className="font-bold text-[15px]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Mantle</span>
              </Link>
              <p className="text-[13px] text-zinc-500 max-w-xs">The all-in-one operating system for trade businesses. Every tool, every trade, fully yours.</p>
            </div>
            <div>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-3.5">Product</p>
              <ul className="space-y-2.5 text-[13px] text-zinc-400">
                <li><Link to="/features" className="hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Sign in</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-3.5">Company</p>
              <ul className="space-y-2.5 text-[13px] text-zinc-400">
                <li><a href="mailto:support@mantlesuite.com" className="hover:text-white transition-colors">Contact</a></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/[0.05] flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[12px] text-zinc-600">© 2026 Mantle. All rights reserved.</p>
            <p className="text-[12px] text-zinc-600">Built by a contractor. For every trade.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
